import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  IconButton, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { Delete, ShoppingCart, Visibility } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const WishlistPage = () => {
  const { t } = useTranslation();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Mock data - replace with API call
  useEffect(() => {
    // Simulate API call to fetch wishlist items
    const mockWishlistItems = [
      {
        id: 1,
        productName: "Office Chair Premium",
        sku: "OCP-001",
        supplier: "Office Supplies Co.",
        estimatedPrice: 299.99,
        quantity: 5,
        priority: "High",
        dateAdded: "2024-01-15",
        notes: "Ergonomic design for better comfort"
      },
      {
        id: 2,
        productName: "Laptop Stand Adjustable",
        sku: "LSA-002",
        supplier: "Tech Solutions Ltd",
        estimatedPrice: 89.50,
        quantity: 10,
        priority: "Medium",
        dateAdded: "2024-01-10",
        notes: "Compatible with 13-17 inch laptops"
      },
      {
        id: 3,
        productName: "Wireless Mouse",
        sku: "WM-003",
        supplier: "Tech Solutions Ltd",
        estimatedPrice: 45.00,
        quantity: 20,
        priority: "Low",
        dateAdded: "2024-01-08",
        notes: "Bluetooth 5.0 connectivity"
      }
    ];
    setWishlistItems(mockWishlistItems);
  }, []);

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setWishlistItems(prev => prev.filter(item => item.id !== itemToDelete.id));
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    // Here you would call your API to delete the item
    // deleteWishlistItem(itemToDelete.id);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const totalEstimatedValue = wishlistItems.reduce(
    (sum, item) => sum + (item.estimatedPrice * item.quantity), 
    0
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          {t("wishlist")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ShoppingCart />}
          sx={{ bgcolor: '#1976d2' }}
          disabled={wishlistItems.length === 0}
        >
          Order All Items
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" color="primary">
              Total Items
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {wishlistItems.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" color="primary">
              Total Estimated Value
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              €{totalEstimatedValue.toFixed(2)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" color="primary">
              High Priority Items
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {wishlistItems.filter(item => item.priority === 'High').length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Wishlist Table */}
      <Card elevation={3}>
        <CardContent sx={{ p: 0 }}>
          {wishlistItems.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No items in wishlist
              </Typography>
              <Typography color="text.secondary">
                Add products to your wishlist to track items you want to order.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Est. Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date Added</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wishlistItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                          {item.productName}
                        </Typography>
                        {item.notes && (
                          <Typography variant="caption" color="text.secondary">
                            {item.notes}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{item.sku}</TableCell>
                    <TableCell>{item.supplier}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>€{item.estimatedPrice.toFixed(2)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      €{(item.estimatedPrice * item.quantity).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.priority} 
                        size="small" 
                        color={getPriorityColor(item.priority)}
                      />
                    </TableCell>
                    <TableCell>{new Date(item.dateAdded).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" color="primary" title="View Details">
                          <Visibility />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error" 
                          title="Delete from Wishlist"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove "{itemToDelete?.productName}" from your wishlist?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WishlistPage;